import { useState, useCallback } from 'react';
import { SelltreezApi } from 'services/api';
import useOrganisation from 'state/organisation/hooks/useOrganisation';
import useAuth from 'state/auth/hooks/useAuth';
import Logger from 'services/logging';
import { formatErrorMessage } from 'utils/errorMessages';

/**
 * Constants for HintsService API endpoints
 */
const HINTS_SERVICE_BASE = '/HintsService/v1.0/rest';
const COMPLETE_TICKET_ENDPOINT = (ticketId: string, userId: string) =>
  `${HINTS_SERVICE_BASE}/ticket/${ticketId}/user/${userId}/completepaidticket`;
const SAVE_TICKET_ENDPOINT = (ticketId: string) =>
  `${HINTS_SERVICE_BASE}/ticket/${ticketId}/restaurant/1/saveticket/`;

/**
 * Interface for complete ticket request payload
 */
interface ICompleteTicketRequest {
  ticketId: string;
  shiftId?: string;
  allowTicketUserReassignment?: boolean;
}

/**
 * Interface for save ticket request payload
 */
interface ISaveTicketRequest {
  ticketId: string;
  userId?: string;
  shiftId?: string;
  allowTicketUserReassignment?: boolean;
}

/**
 * Interface for API response
 */
interface ITicketOperationResponse {
  resultCode: string;
  resultReason: string | null;
  data?: any;
}

/**
 * Custom hook for SellTreez ticket operations (complete and save)
 * Follows merchant-ui patterns and best practices
 */
const useTicketOperations = () => {
  const { organisation } = useOrganisation();
  const { activeOrg } = organisation;
  const { auth } = useAuth();

  const [isCompleting, setIsCompleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Get user ID from auth state
   * Fallback chain: user.user_info.treez_associate_id -> user.id -> localStorage -> error
   * Prioritize treez_associate_id as user.id has format like internal_3nyRL8ccB2c6K5yfrZYSaB
   */
  const getUserId = useCallback((): string => {
    // Try multiple sources - prioritize treez_associate_id
    let userId = auth?.user?.user_info?.treez_associate_id ||
                 auth?.user?.id;

    // Fallback to localStorage if auth doesn't have it
    if (!userId) {
      try {
        const localUser = typeof window !== 'undefined' &&
                         (window as any).localStorage?.getItem('user');
        if (localUser) {
          const parsed = JSON.parse(localUser);
          userId = parsed?.user_info?.treez_associate_id || parsed?.id;
        }
      } catch (e) {
        console.error('Error reading user ID from localStorage:', e);
      }
    }

    // If still no userId, log error
    if (!userId) {
      console.error('No user ID found in auth or localStorage');
      throw new Error('User ID not available. Please ensure you are logged in.');
    }

    return userId;
  }, [auth]);

  /**
   * Complete a paid ticket in SellTreez
   * Endpoint: POST /HintsService/v1.0/rest/ticket/{ticketId}/user/{userId}/completepaidticket
   *
   * @param request - Complete ticket request with ticketId and optional shiftId
   * @returns Promise<ITicketOperationResponse>
   * @throws Error if API call fails
   */
  const completeTicketApi = useCallback(
    async (request: ICompleteTicketRequest): Promise<ITicketOperationResponse> => {
      setIsCompleting(true);
      setError(null);

      try {
        // Validate SellTreez URL
        const baseUrl = activeOrg?.settings.sell_treez_url;
        if (!baseUrl) {
          throw new Error('SellTreez URL not configured for this organization');
        }

        // Get user ID
        const userId = getUserId();

        // Validate required fields
        if (!request.ticketId) {
          throw new Error('Ticket ID is required to complete ticket');
        }

        // Create SelltreezApi instance
        const selltreezApi = new SelltreezApi(baseUrl);

        // Prepare request payload (following SellTreez pattern)
        const payload = {
          shiftId: request.shiftId || null,
          allowTicketUserReassignment: request.allowTicketUserReassignment ?? false,
        };

        // Make API call
        const response = await selltreezApi.post<ITicketOperationResponse>(
          COMPLETE_TICKET_ENDPOINT(request.ticketId, userId),
          payload
        );

        // Check response for errors
        if (response.data.resultCode !== 'SUCCESS') {
          throw new Error(
            response.data.resultReason || 'Failed to complete ticket'
          );
        }

        return response.data;
      } catch (err: any) {
        const rawError =
          err?.response?.data?.resultReason ||
          err?.response?.data?.message ||
          err?.message ||
          'Failed to complete ticket';

        const errorMessage = formatErrorMessage(rawError);

        setError(errorMessage);
        Logger.captureException({
          message: 'Complete ticket failed',
          error: err,
          ticketId: request.ticketId,
        });

        throw new Error(errorMessage);
      } finally {
        setIsCompleting(false);
      }
    },
    [activeOrg, getUserId]
  );

  /**
   * Save a ticket in SellTreez without completing it
   * Endpoint: POST /HintsService/v1.0/rest/ticket/{ticketId}/restaurant/{restaurantId}/saveticket/
   *
   * @param request - Save ticket request with ticketId and optional parameters
   * @returns Promise<ITicketOperationResponse>
   * @throws Error if API call fails
   */
  const saveTicketApi = useCallback(
    async (request: ISaveTicketRequest): Promise<ITicketOperationResponse> => {
      setIsSaving(true);
      setError(null);

      try {
        // Validate SellTreez URL
        const baseUrl = activeOrg?.settings.sell_treez_url;
        if (!baseUrl) {
          throw new Error('SellTreez URL not configured for this organization');
        }

        // Get user ID
        const userId = getUserId();

        // Validate required fields
        if (!request.ticketId) {
          throw new Error('Ticket ID is required to save ticket');
        }

        // Create SelltreezApi instance
        const selltreezApi = new SelltreezApi(baseUrl);

        // Prepare request payload (following SellTreez pattern)
        const payload = {
          userId: request.userId || userId,
          shiftId: request.shiftId || null,
          allowTicketUserReassignment: request.allowTicketUserReassignment ?? false,
        };

        // Make API call
        const response = await selltreezApi.post<ITicketOperationResponse>(
          SAVE_TICKET_ENDPOINT(request.ticketId),
          payload
        );

        // Check response for errors
        if (response.data.resultCode !== 'SUCCESS') {
          throw new Error(
            response.data.resultReason || 'Failed to save ticket'
          );
        }

        return response.data;
      } catch (err: any) {
        const rawError =
          err?.response?.data?.resultReason ||
          err?.response?.data?.message ||
          err?.message ||
          'Failed to save ticket';

        const errorMessage = formatErrorMessage(rawError);

        setError(errorMessage);
        Logger.captureException({
          message: 'Save ticket failed',
          error: err,
          ticketId: request.ticketId,
        });

        throw new Error(errorMessage);
      } finally {
        setIsSaving(false);
      }
    },
    [activeOrg, getUserId]
  );

  /**
   * Reset error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    completeTicket: completeTicketApi,
    saveTicket: saveTicketApi,
    isCompleting,
    isSaving,
    isLoading: isCompleting || isSaving,
    error,
    clearError,
  };
};

export default useTicketOperations;
